// client/src/queryClient.js
import { QueryClient } from '@tanstack/react-query'

/**
 * Centralized React Query configuration
 * Provides sensible defaults for caching, retries, and stale times
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // How long before data is considered stale and needs refetch
      staleTime: 5 * 60 * 1000, // 5 minutes default

      // How long to keep unused data in cache
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

      // Retry failed requests
      retry: 1,

      // Don't refetch on window focus by default (can be overridden per query)
      refetchOnWindowFocus: false,

      // Don't refetch on mount if data is still fresh
      refetchOnMount: false,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
})

/**
 * Query key factory for consistent key generation
 * This ensures we use the same keys across the app
 */
export const queryKeys = {
  // Dashboard data
  /** @returns {string[]} Query key for dashboard data */
  dashboardData: () => ['dashboardData'],
  dashboardSummary: () => ['dashboardSummary'],

  // Leagues
  /** @returns {string[]} Query key for user's enrolled leagues */
  myLeagues: () => ['myLeagues'],

  /** @returns {string[]} Query key for user's divisions across all leagues */
  myDivisions: () => ['myDivisions'],

  /**
   * @param {string|number} leagueId - The league ID
   * @returns {Array<string|number>} Query key for specific league
   */
  league: (leagueId) => ['league', leagueId],

  // Matches
  /** @returns {string[]} Query key for next upcoming match for the user */
  nextMatch: () => ['nextMatch'],

  /** @returns {string[]} Query key for all scheduled matches */
  scheduledMatches: () => ['scheduledMatches'],

  /** @returns {string[]} Query key for all match proposals */
  matchProposals: () => ['matchProposals'],

  /** @returns {string[]} Query key for received match proposals */
  receivedProposals: () => ['matchProposals', 'received'],

  // Awards
  /**
   * @param {number} limit - Maximum number of awards to fetch
   * @returns {Array<string|number>} Query key for recent awards
   */
  recentAwards: (limit) => ['recentAwards', limit],

  // Notifications
  /** @returns {string[]} Query key for unread notification count */
  notificationCount: () => ['notificationCount'],

  /** @returns {string[]} Query key for notifications list */
  notificationsList: () => ['notificationsList'],

  // User
  /** @returns {string[]} Query key for current user's profile */
  userProfile: () => ['userProfile'],
}
